"use client";

import { ComponentPropsWithoutRef, forwardRef } from "react";

import { SVG } from "@saas/shared/icon";
import { classNames } from "@saas/shared/utils";

const Variant = {
  PRIMARY: "primary",
  SECONDARY: "secondary",
  TERTIARY: "tertiary",
  TEXT: "text",
  NEGATIVE: "negative",
} as const;

const Size = {
  SMALL: "small",
  BIG: "big",
  EXTRA_BIG: "extra-big",
} as const;

export interface IconButtonProps extends ComponentPropsWithoutRef<"button"> {
  icon: SVG;
  label: string;
  /**
   * @default "secondary"
   */
  variant?: (typeof Variant)[keyof typeof Variant];
  /**
   * @default "small"
   */
  size?: (typeof Size)[keyof typeof Size];
  /**
   * @default "square"
   */
  fill?: "square" | "circle";
  testid?: string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      label,
      icon: Icon,
      testid,
      variant = "secondary",
      size = "small",
      fill = "square",
      ...props
    }: IconButtonProps,
    ref
  ) => {
    const variantClass = {
      [Variant.PRIMARY]:
        "text-disabled enabled:text-invert enabled:bg-blue-B700 enabled:hover:bg-blue-B600 enabled:focus:bg-blue-B600",
      [Variant.SECONDARY]:
        "text-disabled enabled:bg-blue-B200 enabled:hover:bg-blue-B100",
      [Variant.TEXT]:
        "text-disabled enabled:text-button enabled:hover:text-blue-B600",
      [Variant.TERTIARY]:
        "text-disabled enabled:text-le border-neutral-N300 enabled:hover:text-le enabled:focus:text-button border",
      [Variant.NEGATIVE]:
        "text-disabled enabled:text-invert enabled:bg-red-R500 enabled:hover:bg-red-R400 enabled:focus:bg-red-R400",
    };

    const iconVariantClass = {
      [Variant.PRIMARY]: "text-disabled group-enabled:text-invert",
      [Variant.SECONDARY]:
        "text-disabled group-enabled:text-blue-B700 group-enabled:group-hover:text-blue-B600",
      [Variant.TERTIARY]: "text-disabled group-enabled:text-le",
      [Variant.TEXT]:
        "text-disabled group-enabled:text-blue-B700 group-enabled:group-hover:text-blue-B600",
      [Variant.NEGATIVE]: "text-disabled group-enabled:text-invert",
    };

    const iconSizeClass = {
      [Size.SMALL]: "w-4 h-4",
      [Size.BIG]: "w-6 h-6",
      [Size.EXTRA_BIG]: "w-8 h-8",
    };

    return (
      <button
        {...props}
        ref={ref}
        className={classNames(
          "text-disabled enabled:text-button group",
          fill === "square" && "rounded p-1",
          fill === "circle" && "items-center rounded-full p-2",
          variantClass[variant],
          props.className
        )}
        data-testid={testid}
      >
        <span className={"sr-only"}>{label}</span>
        <Icon
          className={classNames(
            iconSizeClass[size],
            iconVariantClass[variant],
            "m-auto"
          )}
          aria-hidden={true}
        />
      </button>
    );
  }
);

IconButton.displayName = "IconButton";

export default IconButton;
